import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCalendar, FiClock, FiUser, FiMail, FiPhone, FiMessageSquare, FiBriefcase, FiCheck } = FiIcons;

const BookingPage = () => {
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  // Generate available dates (next 14 days)
  const getAvailableDates = () => {
    const dates = [];
    const today = new Date();
    
    for (let i = 1; i <= 14; i++) {
      const nextDate = new Date(today);
      nextDate.setDate(today.getDate() + i);
      
      // Skip weekends (Saturday = 6, Sunday = 0)
      if (nextDate.getDay() !== 0 && nextDate.getDay() !== 6) {
        const formatted = nextDate.toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        });
        
        dates.push({
          date: formatted,
          value: nextDate.toISOString().split('T')[0]
        });
      }
    }
    
    return dates;
  };

  // Generate time slots
  const getTimeSlots = () => {
    const slots = [];
    const startHour = 9; // 9 AM
    const endHour = 17; // 5 PM
    
    for (let hour = startHour; hour <= endHour; hour++) {
      if (hour === 12) continue; // Skip lunch hour
      
      const time = hour < 12 
        ? `${hour}:00 AM` 
        : hour === 12 
          ? `${hour}:00 PM` 
          : `${hour - 12}:00 PM`;
          
      slots.push({
        time: time,
        value: time
      });
    }
    
    return slots;
  };

  const availableDates = getAvailableDates();
  const timeSlots = getTimeSlots();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
  };

  const handleNextStep = () => {
    if (currentStep === 1 && selectedDate && selectedTime) {
      setCurrentStep(2);
    }
  };

  const handlePrevStep = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    if (formData.name && formData.email && selectedDate && selectedTime) {
      // Simulate submission
      setTimeout(() => {
        setIsSubmitted(true);
      }, 1000);
    }
  };

  return (
    <div className="pt-24 pb-16">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center space-y-8"
          >
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="text-5xl md:text-6xl font-bold leading-tight"
            >
              Book a <span className="gradient-text">Demo</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed"
            >
              Schedule a personalized demo with our team to explore how Quantum Leverage can transform your organization.
            </motion.p>
          </motion.div>

          {/* Background Elements */}
          <div className="absolute top-1/3 left-1/4 w-64 h-64 bg-primary-orange/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary-orange/3 rounded-full blur-3xl"></div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          {isSubmitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="glass-effect p-12 text-center space-y-8"
            >
              <div className="w-20 h-20 bg-green-500 rounded-full mx-auto flex items-center justify-center">
                <SafeIcon icon={FiCheck} className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-3xl font-bold">Demo Scheduled!</h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Thank you for booking a demo with Quantum Leverage. We've scheduled your session for:
              </p>
              <div className="bg-gray-900/50 p-6 max-w-md mx-auto">
                <div className="flex items-center space-x-3 mb-4">
                  <SafeIcon icon={FiCalendar} className="w-5 h-5 text-primary-orange" />
                  <span className="font-semibold">{selectedDate}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiClock} className="w-5 h-5 text-primary-orange" />
                  <span className="font-semibold">{selectedTime}</span>
                </div>
              </div>
              <p className="text-gray-300">
                We've sent a confirmation email to <span className="text-primary-orange">{formData.email}</span> with all the details.
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setIsSubmitted(false);
                  setCurrentStep(1);
                  setSelectedDate('');
                  setSelectedTime('');
                  setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    company: '',
                    message: '',
                  });
                }}
                className="bg-primary-orange text-black px-8 py-4 font-semibold text-lg hover:bg-primary-dark transition-all duration-200 inline-block mt-4"
              >
                Schedule Another Demo
              </motion.button>
            </motion.div>
          ) : (
            <div className="space-y-8">
              {/* Progress Steps */}
              <div className="flex justify-center items-center space-x-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${currentStep === 1 ? 'bg-primary-orange text-black' : 'bg-gray-800 text-white'}`}>
                  1
                </div>
                <div className={`w-20 h-1 ${currentStep === 2 ? 'bg-primary-orange' : 'bg-gray-800'}`}></div>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${currentStep === 2 ? 'bg-primary-orange text-black' : 'bg-gray-800 text-white'}`}>
                  2
                </div>
              </div>

              <div className="glass-effect p-8">
                {currentStep === 1 ? (
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-8"
                  >
                    <h2 className="text-2xl font-bold text-center">Select Date & Time</h2>
                    
                    {/* Date Selection */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold flex items-center">
                        <SafeIcon icon={FiCalendar} className="w-5 h-5 mr-2 text-primary-orange" />
                        Available Dates
                      </h3>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                        {availableDates.map((dateObj, index) => (
                          <motion.button
                            key={index}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleDateSelect(dateObj.value)}
                            className={`p-3 text-center transition-all ${
                              selectedDate === dateObj.value
                                ? 'bg-primary-orange text-black'
                                : 'bg-gray-900 hover:border-primary-orange border border-gray-700'
                            }`}
                          >
                            {dateObj.date}
                          </motion.button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Time Selection */}
                    {selectedDate && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-4"
                      >
                        <h3 className="text-lg font-semibold flex items-center">
                          <SafeIcon icon={FiClock} className="w-5 h-5 mr-2 text-primary-orange" />
                          Available Times
                        </h3>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                          {timeSlots.map((timeObj, index) => (
                            <motion.button
                              key={index}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => handleTimeSelect(timeObj.value)}
                              className={`p-3 text-center transition-all ${
                                selectedTime === timeObj.value
                                  ? 'bg-primary-orange text-black'
                                  : 'bg-gray-900 hover:border-primary-orange border border-gray-700'
                              }`}
                            >
                              {timeObj.time}
                            </motion.button>
                          ))}
                        </div>
                      </motion.div>
                    )}
                    
                    <div className="flex justify-end pt-4">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleNextStep}
                        disabled={!selectedDate || !selectedTime}
                        className={`px-8 py-3 font-semibold transition-all ${
                          selectedDate && selectedTime
                            ? 'bg-primary-orange text-black hover:bg-primary-dark'
                            : 'bg-gray-700 text-gray-300 cursor-not-allowed'
                        }`}
                      >
                        Continue
                      </motion.button>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-8"
                  >
                    <h2 className="text-2xl font-bold text-center">Your Information</h2>
                    
                    {/* Selected Date & Time Summary */}
                    <div className="bg-gray-900/50 p-4 flex flex-wrap justify-center gap-6">
                      <div className="flex items-center space-x-3">
                        <SafeIcon icon={FiCalendar} className="w-5 h-5 text-primary-orange" />
                        <span>{new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <SafeIcon icon={FiClock} className="w-5 h-5 text-primary-orange" />
                        <span>{selectedTime}</span>
                      </div>
                    </div>
                    
                    {/* Contact Form */}
                    <form onSubmit={handleSubmit} className="space-y-6">
                      {/* Name */}
                      <div className="space-y-2">
                        <label htmlFor="name" className="flex items-center text-gray-300 font-medium">
                          <SafeIcon icon={FiUser} className="w-5 h-5 mr-2 text-primary-orange" />
                          Your Name *
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-white focus:outline-none focus:border-primary-orange transition-colors"
                        />
                      </div>
                      
                      {/* Email */}
                      <div className="space-y-2">
                        <label htmlFor="email" className="flex items-center text-gray-300 font-medium">
                          <SafeIcon icon={FiMail} className="w-5 h-5 mr-2 text-primary-orange" />
                          Email Address *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-white focus:outline-none focus:border-primary-orange transition-colors"
                        />
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-6">
                        {/* Phone */}
                        <div className="space-y-2">
                          <label htmlFor="phone" className="flex items-center text-gray-300 font-medium">
                            <SafeIcon icon={FiPhone} className="w-5 h-5 mr-2 text-primary-orange" />
                            Phone Number
                          </label>
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-white focus:outline-none focus:border-primary-orange transition-colors"
                          />
                        </div>
                        
                        {/* Company */}
                        <div className="space-y-2">
                          <label htmlFor="company" className="flex items-center text-gray-300 font-medium">
                            <SafeIcon icon={FiBriefcase} className="w-5 h-5 mr-2 text-primary-orange" />
                            Company Name
                          </label>
                          <input
                            type="text"
                            id="company"
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                            className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-white focus:outline-none focus:border-primary-orange transition-colors"
                          />
                        </div>
                      </div>
                      
                      {/* Message */}
                      <div className="space-y-2">
                        <label htmlFor="message" className="flex items-center text-gray-300 font-medium">
                          <SafeIcon icon={FiMessageSquare} className="w-5 h-5 mr-2 text-primary-orange" />
                          What would you like to discuss?
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          rows="4"
                          className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-white focus:outline-none focus:border-primary-orange transition-colors"
                        ></textarea>
                      </div>
                      
                      <div className="flex justify-between pt-4">
                        <motion.button
                          type="button"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={handlePrevStep}
                          className="px-8 py-3 font-semibold border border-gray-600 hover:border-primary-orange hover:text-primary-orange transition-all"
                        >
                          Back
                        </motion.button>
                        
                        <motion.button
                          type="submit"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="bg-primary-orange text-black px-8 py-3 font-semibold hover:bg-primary-dark transition-all"
                        >
                          Schedule Demo
                        </motion.button>
                      </div>
                    </form>
                  </motion.div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-950">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-16"
          >
            <div className="text-center space-y-4">
              <h2 className="text-3xl font-bold">Frequently Asked Questions</h2>
              <div className="h-1 w-24 bg-primary-orange mx-auto"></div>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {[
                {
                  q: 'How long does a demo typically last?',
                  a: 'Our demos are scheduled for 45 minutes, with additional time for questions and discussion as needed.',
                },
                {
                  q: 'Who should attend the demo?',
                  a: 'We recommend including key decision makers and team members who will be working directly with our solution.',
                },
                {
                  q: 'Is there any preparation required before the demo?',
                  a: 'No specific preparation is needed. Our team will customize the demo based on your information provided in the booking form.',
                },
                {
                  q: 'Can I reschedule my demo if needed?',
                  a: 'Yes, you can reschedule your demo by contacting us at least 24 hours before your scheduled time.',
                },
              ].map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  viewport={{ once: true }}
                  className="glass-effect p-6 space-y-3"
                >
                  <h3 className="text-xl font-bold text-primary-orange">{faq.q}</h3>
                  <p className="text-gray-300">{faq.a}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BookingPage;